import { Link, NavLink, useNavigate } from "react-router-dom";
import { Stethoscope, Menu, X, LogOut, User, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const link = ({ isActive }) =>
    `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "text-blue-700 bg-blue-50" : "text-slate-700 hover:text-blue-700 hover:bg-slate-50"
    }`;

  const dashboardPath =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "doctor"
      ? "/doctor/dashboard"
      : "/patient/dashboard";

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-700 to-teal-600 text-white shadow-sm shadow-blue-500/30">
            <Stethoscope className="h-5 w-5" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="text-base font-extrabold tracking-tight text-slate-900">
              Doctor<span className="text-blue-700">+</span>
            </div>
            <div className="-mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-teal-700">
              doctor-plus.in
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <NavLink to="/" end className={link} data-testid="nav-home">Home</NavLink>
          <NavLink to="/doctors" className={link} data-testid="nav-doctors">Find Doctors</NavLink>
          <NavLink to="/specialties" className={link} data-testid="nav-specialties">Specialties</NavLink>
          <NavLink to="/about" className={link} data-testid="nav-about">About</NavLink>
          <NavLink to="/contact" className={link} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {user ? (
            <>
              <button
                data-testid="header-dashboard-btn"
                onClick={() => nav(dashboardPath)}
                className="btn-ghost"
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </button>
              <button
                data-testid="header-logout-btn"
                onClick={() => { logout(); nav("/"); }}
                className="btn-outline"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login-btn" className="btn-ghost">
                <User className="h-4 w-4" /> Login
              </Link>
              <Link to="/doctors" data-testid="header-book-btn" className="btn-primary">
                Book Appointment
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="header-menu-btn"
          onClick={() => setOpen(!open)}
          className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 lg:hidden">
          <div className="flex flex-col gap-1">
            <NavLink to="/" end onClick={() => setOpen(false)} className={link}>Home</NavLink>
            <NavLink to="/doctors" onClick={() => setOpen(false)} className={link}>Find Doctors</NavLink>
            <NavLink to="/specialties" onClick={() => setOpen(false)} className={link}>Specialties</NavLink>
            <NavLink to="/about" onClick={() => setOpen(false)} className={link}>About</NavLink>
            <NavLink to="/contact" onClick={() => setOpen(false)} className={link}>Contact</NavLink>
            <div className="mt-2 flex gap-2">
              {user ? (
                <>
                  <button onClick={() => { setOpen(false); nav(dashboardPath); }} className="btn-ghost flex-1">Dashboard</button>
                  <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="btn-outline flex-1">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline flex-1">Login</Link>
                  <Link to="/doctors" onClick={() => setOpen(false)} className="btn-primary flex-1">Book Now</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
